import { Icon } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, Tag } from "../../../components";
import { SportPlay } from "../../../types";
import { usePlayContext } from "../play-context";
import PlayNamePopover from "./play-name-popover";
import styles from "./play-tab-header.module.scss";

export interface PlayTabHeaderProps<TPlay> {
  play: TPlay;
  showEditPlayDetailsModal: (show: boolean) => void;
}

export const PlayTabHeader = <TPlay extends SportPlay>({
  play,
  showEditPlayDetailsModal,
}: PlayTabHeaderProps<TPlay>) => {
  const { season, team, series, name, tags } = play;
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const handleShowEditPlayDetails = () => {
    showEditPlayDetailsModal(true);
  };

  const { playStateApi } = usePlayContext();
  const { playState, savePlay, updatePartialPlay, isSavingPlay } = playStateApi;

  const location = useLocation();
  const navigate = useNavigate();

  const onDone = useCallback(
    async <TPlay extends SportPlay>(playData?: Partial<TPlay>) => {
      // updating name via popover will stick new name in save request
      if (playState) await savePlay({ ...(playState as TPlay), ...(playData ? playData : {}) }, true);
      if (location.state && location.state.from) navigate(location.state.from);
      else navigate("/home");
    },
    [playState, location, navigate, savePlay]
  );

  const handleClickDone = () => {
    // if play name is "Untitled", open popover to edit name
    if (name === "Untitled") {
      setIsPopoverOpen(true);
    } else {
      onDone();
    }
  };

  const handleClickContinue = () => {
    setIsPopoverOpen(false);
    onDone();
  };

  const handleNameChange = (newName: string) => {
    updatePartialPlay({ name: newName });
  };

  const handleNameChangeViaPopover = async (newName: string) => {
    await onDone({ name: newName });
  };

  return (
    <div className={styles.playTabHeader}>
      <div className={classNames(styles.savingContainer, { [styles?.visible || ""]: !isSavingPlay })}>Saving...</div>
      <div className={styles.playDetails}>
        {tags && tags.map((tag, index) => <Tag tag={tag} key={index} className={styles.tag} />)}
        <span className={styles.playDetailsLabel}>
          {season} {team?.name ? `• ${team?.name}` : ""} {series?.name ? `• ${series?.name}` : ""} •&nbsp;
        </span>
        <EditableName name={name} onSave={handleNameChange} />
      </div>
      <div className={styles.rightContainer}>
        <Icon icon="cog" className={styles.icon} iconSize={24} onClick={handleShowEditPlayDetails} />
        <Popover2
          content={
            <PlayNamePopover
              onSave={handleNameChangeViaPopover}
              onContinue={handleClickContinue}
              isSaving={isSavingPlay}
            />
          }
          isOpen={isPopoverOpen}
          placement="auto"
          interactionKind="click"
        >
          <Button id="playEditDoneButton" onClick={handleClickDone}>
            Done
          </Button>
        </Popover2>
      </div>
    </div>
  );
};

export interface EditableNameProps {
  name?: string;
  onSave: (name: string) => void;
}

export const EditableName = ({ name = "", onSave }: EditableNameProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>(name);

  const handleOnBlur = () => {
    setIsEditing(false);
    if (newName !== name && newName.length > 0) {
      onSave(newName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleOnBlur();
    }
  };

  if (isEditing) {
    return (
      <input
        id="playNameInput"
        type="text"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onBlur={handleOnBlur}
        onKeyDown={handleKeyDown}
        autoFocus
      />
    );
  } else {
    return (
      <div id="playName" onDoubleClick={() => setIsEditing(true)}>
        {name}
      </div>
    );
  }
};
